/**
 * Rutas de Softfi.
 *
 * Las URLs están en español a propósito: son parte de la experiencia del
 * producto, y un dueño de bodega que ve "/catalogo" en la barra del navegador
 * entiende dónde está.
 *
 * Hay tres tipos de ruta:
 *   - Públicas   -> login, registro, aceptar invitación
 *   - Protegidas -> requieren sesión con un negocio activo
 *   - De tránsito-> el selector de negocio (hay sesión pero aún no hay negocio)
 */

import { esTrozoQueNoLlego, recargarPorVersionNueva } from "@/lib/recargaPorVersion";
import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import { ConPermiso } from "@/components/ConPermiso";
import { PERMISOS_DEL_CALENDARIO } from "@/features/calendario/calendario";
import { EnTodosLosNegocios } from "@/features/grupo/EnTodosLosNegocios";

/** Las secciones de Reportes a las que se manda a quien no tiene el Resumen. */
const OTRAS_SECCIONES_DE_REPORTES = [
  { permiso: "reports.detalle", a: "/reportes/detalle" },
  { permiso: "reports.utilidad", a: "/reportes/utilidad" },
  { permiso: "reports.cuentas", a: "/reportes/cuentas" },
  { permiso: "reports.estadisticas", a: "/reportes/estadisticas" },
  { permiso: "reports.rotacion", a: "/reportes/rotacion" },
  { permiso: "reports.prediccion", a: "/reportes/prediccion" },
  { permiso: "reports.historial_costos", a: "/reportes/costos" },
  { permiso: "reports.precios", a: "/reportes/precios" },
];

/**
 * Adónde va en «Todas las sucursales» quien no ve las ventas (2026-10-17): el
 * empleado de Cashea entra a mirar existencias, y el Resumen son las ventas.
 */
const EN_LUGAR_DEL_RESUMEN_DE_TODAS = [
  { permiso: "inventory.view", a: "/sucursales/inventario" },
  { permiso: "reports.view_costs", a: "/sucursales/dinero" },
  { permiso: "pos.sell", a: "/sucursales/por-cobrar" },
  { permiso: "reports.sucursales", a: "/sucursales/calendario" },
];
import { AppLayout } from "./AppLayout";
import { AuthLayout } from "./AuthLayout";
import { Cargando } from "@/components/ui";
import { useAuth } from "@/stores/auth";

// Pantallas
import { EntrarPage } from "@/features/auth/EntrarPage";
import { InicioPage } from "@/features/inicio/InicioPage";
import { PosPage } from "@/features/pos/PosPage";

/** Con cualquiera de los dos se entra a la agenda y a los pacientes del consultorio. */
const PERMISOS_DEL_CONSULTORIO = ["consultorio.agenda", "consultorio.atender"];

/**
 * Guarda de ruta: exige sesión iniciada Y un negocio activo.
 *
 * Es solo comodidad de navegación. La seguridad real la aplica el backend en
 * cada endpoint: aunque alguien forzara la URL, no recibiría ningún dato.
 */
function RutaProtegida() {
  const { user, tenant, needsOnboarding, soporte, cargando } = useAuth();
  const ubicacion = useLocation();

  // Mientras comprobamos si hay sesión guardada, no decidimos nada: si
  // redirigiéramos ahora, quien recarga la página acabaría en el login aunque
  // tuviera sesión válida.
  if (cargando) return <PantallaDeCarga />;

  if (!user) {
    // `state.desde` permite volver a donde iba después de iniciar sesión.
    return <Navigate to="/entrar" replace state={{ desde: ubicacion.pathname }} />;
  }

  // ── ¿Confirmó el correo? ──────────────────────────────────────────────
  //
  // Va ANTES que la elección de negocio y que la bienvenida, y el `return`
  // directo del segundo caso también importa: sin verificar, el backend
  // responde 403 en TODOS los endpoints del producto. Cualquier pantalla que
  // se enseñe antes del código sería una pantalla rota.
  //
  // ⚠️ El `key` con la ruta, por lo mismo que el de más abajo: `<Navigate>`
  // solo navega una vez por instancia.
  if (user.email_verified_at === null) {
    if (ubicacion.pathname !== "/verificar") {
      return <Navigate key={ubicacion.pathname} to="/verificar" replace />;
    }
    return <Outlet />;
  }

  // ⚠️ EL PANEL INTERNO NO NECESITA UN NEGOCIO PROPIO.
  //
  // Quien administra Softfi no tiene por qué ser cliente de Softfi. Antes esto
  // le exigía un negocio a todo el mundo, y el resultado fue una trampa: al
  // crear la cuenta de administrador hubo que inventarle un negocio «Softfi»
  // —que además le contaba como cliente en sus propias métricas— y en cuanto
  // lo borró, se quedó fuera de su propio panel.
  //
  // No es un agujero: quien manda de verdad es el servidor, que comprueba la
  // tabla `platform_admins` en cada petición del panel. `AdminLayout` además
  // pregunta antes de pintar nada, así que quien no sea administrador rebota
  // igual — solo que ahora rebota por el motivo correcto.
  const vaAlPanel = ubicacion.pathname.startsWith("/admin");

  if (!tenant && !vaAlPanel) return <Navigate to="/seleccionar-negocio" replace />;

  // ── ¿Softfi ya aprobó el negocio? ─────────────────────────────────────
  //
  // Va DESPUÉS de elegir negocio y no antes: alguien puede tener uno aprobado
  // y otro esperando, y en ese caso lo que no puede es que el pendiente le
  // bloquee el que sí funciona.
  //
  // ⚠️ Sin esta pantalla, el backend responde 403 en todo y lo que se vería es
  // la aplicación entera cargando en blanco con errores rojos. La persona
  // pensaría que Softfi está roto — y no lo está: está esperándonos a nosotros.
  if (tenant && (tenant.status === "pending" || tenant.status === "rechazado")) {
    if (ubicacion.pathname !== "/esperando-aprobacion") {
      return <Navigate key={ubicacion.pathname} to="/esperando-aprobacion" replace />;
    }
    return <Outlet />;
  }

  // Negocio recién creado: al wizard de bienvenida. Se puede saltar en cualquier
  // momento — obligar a completarlo sería la forma más rápida de perder al cliente.
  //
  // ⚠️ El `key` con la ruta actual NO es decorativo, y aquí está por qué:
  //
  // `<Navigate>` no navega al dibujarse, lo hace en un efecto que corre **una
  // sola vez** — sus dependencias son el destino, que nunca cambia. Si algo
  // devuelve la URL a donde estaba (una pantalla que navega por su cuenta justo
  // después de crear un negocio, por ejemplo), React reutiliza esta misma
  // instancia, el efecto no vuelve a dispararse, y `<Navigate>` se queda
  // dibujando `null`.
  //
  // Eso deja la aplicación entera en blanco **sin un solo error en la consola**,
  // que es la peor forma posible de fallar. Con el `key` cambiando, React monta
  // un componente nuevo y su efecto sí corre.
  // `!vaAlPanel` por lo mismo de arriba: un administrador sin negocio propio
  // tiene `needsOnboarding` en cierto, y sin esto el panel lo mandaría a crear
  // un negocio cada vez que intenta entrar.
  //
  // ⚠️ `!soporte`: entrando al negocio de un cliente que todavía no terminó su
  // configuración, esto nos metía en SU asistente de bienvenida. Dos problemas,
  // y el segundo es el grave:
  //
  //   1. El aviso de «estás en un negocio ajeno» no se ve ahí, porque el
  //      asistente tiene su propio armazón.
  //   2. Se le puede completar la configuración sin querer — eligiéndole la
  //      moneda o el modo de trabajo a alguien que todavía no decidió. Y el
  //      asistente corre una sola vez: no hay forma de devolvérselo.
  //
  // El soporte entra a MIRAR. Que el cliente termine su bienvenida cuando le
  // toque.
  if (needsOnboarding && !vaAlPanel && !soporte && ubicacion.pathname !== "/bienvenida") {
    return <Navigate key={ubicacion.pathname} to="/bienvenida" replace />;
  }

  return <Outlet />;
}

/** Guarda inversa: si YA hay sesión, el login y el registro no tienen sentido. */
function RutaPublica() {
  const { user, tenant, cargando } = useAuth();

  if (cargando) return <PantallaDeCarga />;
  if (user && tenant) return <Navigate to="/" replace />;
  if (user && !tenant) return <Navigate to="/seleccionar-negocio" replace />;

  return <Outlet />;
}

function PantallaDeCarga() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <Cargando texto="Cargando Softfi…" />
    </div>
  );
}

/**
 * Carga una pantalla solo cuando alguien entra en ella.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ LAS PANTALLAS NO SE IMPORTAN TODAS DE GOLPE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Antes este archivo tenía 78 imports directos, y eso metía las 96 pantallas de
 * Softfi en un único archivo de JavaScript de 1,35 MB. Un cajero que entra a
 * cobrar se descargaba igual el editor del mapa de mesas, los reportes de
 * utilidad y el panel de administración de la plataforma.
 *
 * Medido sobre `app.softfi.online` en un equipo de 16 núcleos: 2.154 ms hasta
 * ver algo, y **ningún evento de pintado** en el medio — o sea, dos segundos de
 * pantalla en blanco. En la laptop de gama baja de una bodega eso son entre seis
 * y diez segundos en los que parece que Softfi no abre.
 *
 * ⚠️ TRES PANTALLAS NO SE PARTEN, y no es un descuido:
 *
 *   - `EntrarPage`  → es lo primero que ve todo el mundo.
 *   - `InicioPage`  → es donde cae cualquiera al entrar.
 *   - `PosPage`     → es la caja. El cajero abre Softfi PARA esto, y meterle un
 *                     viaje extra a la red justo ahí sería cambiar dos segundos
 *                     de arranque por una espera con el cliente delante.
 *
 * Todo lo demás llega cuando hace falta, que es casi nunca en mitad de una
 * venta.
 */
function perezosa(
  importar: () => Promise<Record<string, unknown>>,
  nombre: string,
  props: Record<string, unknown> = {},
) {
  // ⚠️ `lazy()` va AQUÍ, en el armado de las rutas, que corre una sola vez al
  // cargar el módulo. Si alguien mueve esta llamada dentro de un render, cada
  // dibujado crearía un componente nuevo: la pantalla se desmontaría y volvería
  // a montar sola, perdiendo lo que la persona estuviera escribiendo.
  const Pantalla = lazy(async () => {
    try {
      return { default: (await importar())[nombre] as ComponentType<Record<string, unknown>> };
    } catch (error) {
      // ⚠️ La pantalla es de una versión que ya no está en el servidor (otra
      // pestaña actualizó). Se recarga una vez y mientras tanto se queda el
      // «cargando», en vez del error en inglés. Ver `recargaPorVersion.ts`.
      if (esTrozoQueNoLlego(error) && recargarPorVersionNueva()) {
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });

  return (
    // El aviso ocupa alto propio en vez de estirarse a la pantalla completa:
    // aquí dentro ya está dibujado el menú lateral, y un `min-h-dvh` empujaría
    // la página hacia abajo y daría un salto feo al terminar de cargar.
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center">
          <Cargando />
        </div>
      }
    >
      <Pantalla {...props} />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // --- Públicas ---
  {
    element: <RutaPublica />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: "/entrar", element: <EntrarPage /> },
          { path: "/registro", element: perezosa(() => import("@/features/auth/RegistroPage"), "RegistroPage") },
        ],
      },
    ],
  },

  // Estas dos son públicas SIN la guarda inversa, y por la misma razón: tener
  // sesión abierta no debe impedir usarlas.
  //
  //   - Aceptar invitación: alguien con sesión puede unirse a un segundo negocio.
  //   - Recuperar contraseña: quien pincha el enlace del correo desde el mismo
  //     aparato donde ya entró se quedaría sin poder cambiarla — que es justo el
  //     caso de "no me acuerdo de la clave en la computadora del local".
  {
    element: <AuthLayout />,
    children: [
      { path: "/aceptar-invitacion", element: perezosa(() => import("@/features/auth/AceptarInvitacionPage"), "AceptarInvitacionPage") },
      { path: "/recuperar", element: perezosa(() => import("@/features/auth/RecuperarPage"), "RecuperarPage") },
    ],
  },

  // --- Selector de negocio (hay sesión, falta elegir negocio) ---
  {
    element: <AuthLayout />,
    children: [{ path: "/seleccionar-negocio", element: perezosa(() => import("@/features/auth/SeleccionarNegocioPage"), "SeleccionarNegocioPage") }],
  },

  // --- Confirmar el correo ---
  //
  // Necesita sesión (el token identifica a quién verificar) pero va con el
  // diseño de las pantallas de entrar, no con la navegación de la aplicación:
  // quien está aquí todavía no puede usar nada de lo que enseñaría esa barra.
  {
    element: <RutaProtegida />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: "/verificar", element: perezosa(() => import("@/features/auth/VerificarPage"), "VerificarPage") },
          { path: "/esperando-aprobacion", element: perezosa(() => import("@/features/auth/EsperandoAprobacionPage"), "EsperandoAprobacionPage") },
        ],
      },
    ],
  },

  // --- Wizard de bienvenida (sin la navegación de la app, para no distraer) ---
  {
    element: <RutaProtegida />,
    children: [{ path: "/bienvenida", element: perezosa(() => import("@/features/onboarding/BienvenidaPage"), "BienvenidaPage") }],
  },

  // --- Aplicación ---
  {
    element: <RutaProtegida />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "/", element: <InicioPage /> },
          { path: "/vender", element: <PosPage /> },
          { path: "/ventas", element: perezosa(() => import("@/features/pos/VentasPage"), "VentasPage") },
          { path: "/ventas/registro", element: <ConPermiso permiso={"reports.registro_ventas"}>{perezosa(() => import("@/features/pos/RegistroDeVentasPage"), "RegistroDeVentasPage")}</ConPermiso> },
          { path: "/cotizaciones", element: <ConPermiso permiso={"pos.cotizaciones"}>{perezosa(() => import("@/features/cotizaciones/CotizacionesPage"), "CotizacionesPage")}</ConPermiso> },
          { path: "/ventas-sin-enviar", element: perezosa(() => import("@/features/pos/VentasSinEnviarPage"), "VentasSinEnviarPage") },
          { path: "/sofi", element: perezosa(() => import("@/features/sofi/SofiPage"), "SofiPage") },
          { path: "/reportes", element: <ConPermiso permiso="reports.resumen" enSuLugar={OTRAS_SECCIONES_DE_REPORTES}>{perezosa(() => import("@/features/reportes/ReportesPage"), "ReportesPage")}</ConPermiso> },
          { path: "/reportes/costos", element: <ConPermiso permiso={"reports.historial_costos"}>{perezosa(() => import("@/features/reportes/CostosPage"), "CostosPage")}</ConPermiso> },
          { path: "/reportes/cuentas", element: <ConPermiso permiso={"reports.cuentas"}>{perezosa(() => import("@/features/reportes/CuentasPage"), "CuentasPage")}</ConPermiso> },
          { path: "/reportes/estadisticas", element: <ConPermiso permiso={"reports.estadisticas"}>{perezosa(() => import("@/features/reportes/EstadisticasPage"), "EstadisticasPage")}</ConPermiso> },
          // El «Ver todo» de lo más y lo menos vendido (2026-10-12).
          { path: "/reportes/estadisticas/ranking", element: <ConPermiso permiso={"reports.estadisticas"}>{perezosa(() => import("@/features/reportes/RankingDeProductosPage"), "RankingDeProductosPage")}</ConPermiso> },
          // El reporte de precios: cada producto en cada lista (2026-10-12).
          { path: "/reportes/precios", element: <ConPermiso permiso={"reports.precios"}>{perezosa(() => import("@/features/reportes/ReporteDePreciosPage"), "ReporteDePreciosPage")}</ConPermiso> },
          { path: "/reportes/rotacion", element: <ConPermiso permiso={"reports.rotacion"}>{perezosa(() => import("@/features/reportes/RotacionPage"), "RotacionPage")}</ConPermiso> },
          { path: "/reportes/prediccion", element: <ConPermiso permiso={"reports.prediccion"}>{perezosa(() => import("@/features/reportes/PrediccionPage"), "PrediccionPage")}</ConPermiso> },
          { path: "/reportes/detalle", element: <ConPermiso permiso={"reports.detalle"}>{perezosa(() => import("@/features/reportes/ReporteDetalladoPage"), "ReporteDetalladoPage")}</ConPermiso> },
          { path: "/reportes/utilidad", element: <ConPermiso permiso={"reports.utilidad"}>{perezosa(() => import("@/features/reportes/UtilidadPage"), "UtilidadPage")}</ConPermiso> },
          // ── «TODOS»: SU PROPIO ESPACIO, CON SU PROPIO MENÚ ──────────────
          //
          // Cuelgan de `/grupo` y no de la raíz porque `AppLayout` mira ese
          // prefijo para cambiar la barra lateral entera. Antes eran dos
          // pantallas sueltas con el menú de la Barbería El Corte todavía al
          // lado: el dueño miraba las cifras de sus cuatro negocios y tenía un
          // «Inventario» pegado que era de uno solo.
          // «Mis negocios» como «Todas las sucursales» (2026-10-03). Sin
          // Premium, el Resumen enseña la versión corta de `TodosPage`. Las
          // demás pestañas son las MISMAS pantallas de «Todas las sucursales»,
          // con un negocio donde allí va una sucursal (`EnTodosLosNegocios`).
          { path: "/grupo", element: perezosa(() => import("@/features/grupo/ResumenDeNegociosPage"), "ResumenDeNegociosPage") },
          { path: "/grupo/ventas", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/VentasDeSucursalesPage"), "VentasDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/clientes", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/ClientesDeSucursalesPage"), "ClientesDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/dinero", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/DineroDeSucursalesPage"), "DineroDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/por-cobrar", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/PorCobrarDeSucursalesPage"), "PorCobrarDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/por-pagar", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/PorPagarDeSucursalesPage"), "PorPagarDeSucursalesPage")}</EnTodosLosNegocios> },
          // El calendario de todos los negocios (2026-10-07): la misma pantalla de «Todas».
          { path: "/grupo/calendario", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/CalendarioDeSucursalesPage"), "CalendarioDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/inventario", element: <EnTodosLosNegocios>{perezosa(() => import("@/features/sucursales/InventarioDeSucursalesPage"), "InventarioDeSucursalesPage")}</EnTodosLosNegocios> },
          { path: "/grupo/negocios", element: perezosa(() => import("@/features/reportes/NegociosDelGrupoPage"), "NegociosDelGrupoPage") },
          {
            path: "/grupo/sofi",
            element: perezosa(() => import("@/features/sofi/SofiPage"), "SofiPage", {
              ambito: "grupo",
            }),
          },
          // ── «TODAS LAS SUCURSALES»: SU PROPIO ESPACIO (2026-09-29) ─────────
          //
          // Como «Todos» pero dentro de UN negocio: sus sucursales juntas. Se
          // entra desde «Sucursales → Todas» del selector, y `AppLayout` mira
          // el prefijo `/sucursales` para poner su barra lateral.
          { path: "/sucursales", element: <ConPermiso permiso={["reports.sucursales", "reports.resumen"]} enSuLugar={EN_LUGAR_DEL_RESUMEN_DE_TODAS}>{perezosa(() => import("@/features/sucursales/ResumenDeSucursalesPage"), "ResumenDeSucursalesPage")}</ConPermiso> },
          { path: "/sucursales/dinero", element: <ConPermiso permiso={["reports.sucursales", "reports.view_costs"]}>{perezosa(() => import("@/features/sucursales/DineroDeSucursalesPage"), "DineroDeSucursalesPage")}</ConPermiso> },
          // El calendario de todas (2026-10-07): el de Administrar, con todas juntas.
          { path: "/sucursales/calendario", element: <ConPermiso permiso={"reports.sucursales"}>{perezosa(() => import("@/features/sucursales/CalendarioDeSucursalesPage"), "CalendarioDeSucursalesPage")}</ConPermiso> },
          { path: "/sucursales/inventario", element: <ConPermiso permiso={["reports.sucursales", "inventory.view"]}>{perezosa(() => import("@/features/sucursales/InventarioDeSucursalesPage"), "InventarioDeSucursalesPage")}</ConPermiso> },
          // Un producto en todas las sucursales, buscado o escaneado (2026-10-07).
          { path: "/sucursales/consultor", element: <ConPermiso permiso={["reports.sucursales", "inventory.view"]}>{perezosa(() => import("@/features/sucursales/ConsultorDeSucursalesPage"), "ConsultorDeSucursalesPage")}</ConPermiso> },
          // Las pestañas nuevas del 2026-10-02: ventas, y lo que se cobra y se paga en todas.
          { path: "/sucursales/ventas", element: <ConPermiso permiso={["reports.sucursales", "reports.resumen"]}>{perezosa(() => import("@/features/sucursales/VentasDeSucursalesPage"), "VentasDeSucursalesPage")}</ConPermiso> },
          { path: "/sucursales/por-cobrar", element: <ConPermiso permiso={["reports.sucursales", "pos.sell"]}>{perezosa(() => import("@/features/sucursales/PorCobrarDeSucursalesPage"), "PorCobrarDeSucursalesPage")}</ConPermiso> },
          // Segunda tanda (2026-10-02): los clientes de todas.
          { path: "/sucursales/clientes", element: <ConPermiso permiso={["reports.sucursales", "pos.sell", "reports.resumen"]}>{perezosa(() => import("@/features/sucursales/ClientesDeSucursalesPage"), "ClientesDeSucursalesPage")}</ConPermiso> },
          { path: "/sucursales/por-pagar", element: <ConPermiso permiso={["reports.sucursales", "finanzas.por_pagar"]}>{perezosa(() => import("@/features/sucursales/PorPagarDeSucursalesPage"), "PorPagarDeSucursalesPage")}</ConPermiso> },
          // Rotación y predicción de todas las sucursales (2026-10-05).
          { path: "/sucursales/rotacion", element: <ConPermiso permiso={["reports.sucursales", "reports.rotacion"]}>{perezosa(() => import("@/features/sucursales/RotacionDeSucursalesPage"), "RotacionDeSucursalesPage")}</ConPermiso> },
          { path: "/sucursales/prediccion", element: <ConPermiso permiso={["reports.sucursales", "reports.prediccion"]}>{perezosa(() => import("@/features/sucursales/PrediccionDeSucursalesPage"), "PrediccionDeSucursalesPage")}</ConPermiso> },
          // Sofi mirando todas las sucursales a la vez (2026-10-03, lo pidió el dueño).
          {
            path: "/sucursales/sofi",
            element: <ConPermiso permiso={["reports.sucursales", "reports.resumen"]}>{perezosa(() => import("@/features/sofi/SofiPage"), "SofiPage", { ambito: "sucursales" })}</ConPermiso>,
          },
          { path: "/devoluciones", element: perezosa(() => import("@/features/devoluciones/DevolucionesPage"), "DevolucionesPage") },
          { path: "/credito", element: perezosa(() => import("@/features/fiado/FiadoPage"), "FiadoPage") },
          // El expediente de quien debe: cada venta a crédito en su carpeta, y con
          // Cashea y parecidas, su plan de cuotas.
          { path: "/credito/:customerId", element: perezosa(() => import("@/features/fiado/ExpedientePage"), "ExpedientePage") },
          { path: "/apartados", element: perezosa(() => import("@/features/apartados/ApartadosPage"), "ApartadosPage") },
          // El nombre viejo sigue funcionando: si alguien tiene «/fiado»
          // guardado o escrito en un mensaje, no le va a dar pantalla en blanco.
          { path: "/fiado", element: <Navigate to="/credito" replace /> },
          { path: "/por-pagar", element: <ConPermiso permiso={"finanzas.por_pagar"}>{perezosa(() => import("@/features/pagos/PorPagarPage"), "PorPagarPage")}</ConPermiso> },
          { path: "/por-pagar/beneficiario", element: <ConPermiso permiso={"finanzas.por_pagar"}>{perezosa(() => import("@/features/pagos/HistorialDeBeneficiarioPage"), "HistorialDeBeneficiarioPage")}</ConPermiso> },
          { path: "/cuentas", element: <ConPermiso permiso={"finanzas.cuentas"}>{perezosa(() => import("@/features/cuentas/CuentasPage"), "CuentasDelNegocioPage")}</ConPermiso> },
          // Los de todas las cuentas juntas (2026-10-21). Antes que `/cuentas/:id`, que no la confunde: una ruta fija gana.
          { path: "/cuentas/movimientos", element: <ConPermiso permiso={"finanzas.cuentas"}>{perezosa(() => import("@/features/cuentas/MovimientosDeCuentasPage"), "MovimientosDeCuentasPage")}</ConPermiso> },
          { path: "/cuentas/:id", element: <ConPermiso permiso={"finanzas.cuentas"}>{perezosa(() => import("@/features/cuentas/LibroDeCuentaPage"), "LibroDeCuentaPage")}</ConPermiso> },
          { path: "/caja", element: perezosa(() => import("@/features/caja/CajaPage"), "CajaPage") },
          { path: "/caja/historial", element: perezosa(() => import("@/features/caja/HistorialDeCajaPage"), "HistorialDeCajaPage") },
          { path: "/caja/cajas", element: <ConPermiso permiso={"tenant.cajas"}>{perezosa(() => import("@/features/caja/CajasPage"), "CajasPage")}</ConPermiso> },
          { path: "/gastos", element: perezosa(() => import("@/features/caja/GastosPage"), "GastosPage") },
          { path: "/nomina", element: perezosa(() => import("@/features/nomina/NominaPage"), "NominaPage") },
          { path: "/clientes", element: perezosa(() => import("@/features/clientes/ClientesPage"), "ClientesPage") },
          { path: "/clientes/importar", element: <ConPermiso permiso={"catalog.importar"}>{perezosa(() => import("@/features/clientes/ImportarClientesPage"), "ImportarClientesPage")}</ConPermiso> },
          { path: "/salon", element: perezosa(() => import("@/features/mesas/MesasPage"), "MesasPage") },
          { path: "/cocina", element: perezosa(() => import("@/features/mesas/CocinaPage"), "CocinaPage") },
          { path: "/reservaciones", element: <ConPermiso permiso={"resto.reservaciones"}>{perezosa(() => import("@/features/reservaciones/ReservacionesPage"), "ReservacionesPage")}</ConPermiso> },
          { path: "/merma", element: <ConPermiso permiso={"inventory.conteo"}>{perezosa(() => import("@/features/conteos/ControlDeMermaPage"), "ControlDeMermaPage")}</ConPermiso> },
          { path: "/merma/:conteoId", element: <ConPermiso permiso={"inventory.conteo"}>{perezosa(() => import("@/features/conteos/ConteoPage"), "ConteoPage")}</ConPermiso> },
          // Modo consultorio (2026-10-30). La agenda y los pacientes los abre la
          // recepción o el personal médico; la consulta, solo quien atiende. El
          // servidor cierra cada puerta igual: esto solo evita la pantalla vacía.
          { path: "/agenda", element: <ConPermiso alguno permiso={PERMISOS_DEL_CONSULTORIO}>{perezosa(() => import("@/features/consultorio/AgendaPage"), "AgendaPage")}</ConPermiso> },
          { path: "/sala-de-espera", element: <ConPermiso alguno permiso={PERMISOS_DEL_CONSULTORIO}>{perezosa(() => import("@/features/consultorio/SalaDeEsperaPage"), "SalaDeEsperaPage")}</ConPermiso> },
          { path: "/pacientes", element: <ConPermiso alguno permiso={PERMISOS_DEL_CONSULTORIO}>{perezosa(() => import("@/features/consultorio/PacientesPage"), "PacientesPage")}</ConPermiso> },
          { path: "/pacientes/:pacienteId", element: <ConPermiso alguno permiso={PERMISOS_DEL_CONSULTORIO}>{perezosa(() => import("@/features/consultorio/HistoriaClinicaPage"), "HistoriaClinicaPage")}</ConPermiso> },
          { path: "/pacientes/:pacienteId/consulta/:notaId", element: <ConPermiso permiso={"consultorio.atender"}>{perezosa(() => import("@/features/consultorio/ConsultaPage"), "ConsultaPage")}</ConPermiso> },
          { path: "/catalogo", element: perezosa(() => import("@/features/catalogo/CatalogoPage"), "CatalogoPage") },
          { path: "/catalogo/categorias", element: perezosa(() => import("@/features/catalogo/CategoriasPage"), "CategoriasPage") },
          /*
            ⚠️ IMPUESTOS TIENE SU PROPIA DIRECCIÓN, Y ANTES COLGABA DE CATÁLOGO.

            Se mudó a Dinero en el menú, pero la dirección seguía siendo
            `/catalogo/impuestos`. La barra lateral despliega una sección cuando
            la dirección empieza por la suya, así que al tocar Impuestos se abría
            Catálogo. El dueño lo cazó en el acto.

            La vieja sigue funcionando y manda aquí, como `/fiado`.
          */
          { path: "/impuestos", element: perezosa(() => import("@/features/catalogo/ImpuestosPage"), "ImpuestosPage") },
          { path: "/catalogo/impuestos", element: <Navigate to="/impuestos" replace /> },
          { path: "/catalogo/opciones", element: <ConPermiso permiso={"catalog.opciones"}>{perezosa(() => import("@/features/catalogo/GruposDeOpcionesPage"), "GruposDeOpcionesPage")}</ConPermiso> },
          { path: "/etiquetas", element: <ConPermiso permiso={"catalog.etiquetas"}>{perezosa(() => import("@/features/etiquetas/EtiquetasPage"), "EtiquetasPage")}</ConPermiso> },
          { path: "/catalogo/precios", element: <ConPermiso permiso={"catalog.precios"}>{perezosa(() => import("@/features/catalogo/PreciosPage"), "PreciosPage")}</ConPermiso> },
          { path: "/catalogo/codigos", element: <ConPermiso permiso={"catalog.codigos"}>{perezosa(() => import("@/features/catalogo/CodigosPage"), "CodigosPage")}</ConPermiso> },
          { path: "/inventario", element: perezosa(() => import("@/features/inventario/InventarioPage"), "InventarioPage") },
          // Producción por tandas (2026-10-21): fórmulas y registro.
          { path: "/produccion", element: <ConPermiso permiso={"inventory.produccion"}>{perezosa(() => import("@/features/produccion/ProduccionPage"), "ProduccionPage")}</ConPermiso> },
          { path: "/inventario/entradas", element: <ConPermiso permiso={"inventory.entradas"}>{perezosa(() => import("@/features/inventario/EntradasPage"), "EntradasPage")}</ConPermiso> },
          { path: "/inventario/cargar-existencias", element: <ConPermiso permiso={"inventory.ajustes"}>{perezosa(() => import("@/features/inventario/CargarExistenciasPage"), "CargarExistenciasPage")}</ConPermiso> },
          { path: "/inventario/movimientos", element: perezosa(() => import("@/features/inventario/MovimientosPage"), "MovimientosPage") },
          // Traslados (2026-09-29): antes «Mandar a otra sucursal» en `/inventario/traspasos`.
          { path: "/inventario/traslados", element: perezosa(() => import("@/features/inventario/traslados/TrasladosPage"), "TrasladosPage") },
          // Recoger un traslado en el almacén, por recorrido (fase 2, 2026-10-08).
          { path: "/inventario/traslados/:id/recoger", element: <ConPermiso permiso={"inventory.traslados"}>{perezosa(() => import("@/features/inventario/ubicaciones/RecogerPage"), "RecogerPage")}</ConPermiso> },
          // Contar un pasillo, una zona o un lugar (fase 2, 2026-10-08).
          { path: "/inventario/ubicaciones/contar", element: <ConPermiso permiso={"inventory.conteo"}>{perezosa(() => import("@/features/inventario/ubicaciones/ContarPage"), "ContarPage")}</ConPermiso> },
          { path: "/inventario/plano", element: <ConPermiso permiso={"inventory.view"}>{perezosa(() => import("@/features/inventario/ubicaciones/PlanoDelAlmacenPage"), "PlanoDelAlmacenPage")}</ConPermiso> },
          // Dónde está cada cosa dentro de un almacén (2026-10-07).
          { path: "/inventario/ubicaciones", element: <ConPermiso permiso={"inventory.view"}>{perezosa(() => import("@/features/inventario/ubicaciones/UbicacionesPage"), "UbicacionesPage")}</ConPermiso> },
          { path: "/inventario/almacenes", element: <ConPermiso permiso={"tenant.sucursales"}>{perezosa(() => import("@/features/inventario/AlmacenesPage"), "AlmacenesPage")}</ConPermiso> },
          { path: "/inventario/traslados/nuevo", element: <ConPermiso permiso={"inventory.traslados"}>{perezosa(() => import("@/features/inventario/traslados/NuevoTrasladoPage"), "NuevoTrasladoPage")}</ConPermiso> },
          { path: "/inventario/traslados/:id", element: perezosa(() => import("@/features/inventario/traslados/TrasladoPage"), "TrasladoPage") },
          { path: "/inventario/traspasos", element: <Navigate to="/inventario/traslados" replace /> },
          { path: "/inventario/proveedores", element: perezosa(() => import("@/features/inventario/ProveedoresPage"), "ProveedoresPage") },
          { path: "/catalogo-web", element: <ConPermiso permiso={"catalog.web"}>{perezosa(() => import("@/features/catalogoWeb/CatalogoWebPage"), "CatalogoWebPage")}</ConPermiso> },
          { path: "/autoservicio", element: <ConPermiso permiso={"catalog.view"}>{perezosa(() => import("@/features/autoservicio/AutoservicioPage"), "AutoservicioPage")}</ConPermiso> },
          // Turnos y pantalla publicitaria (2026-11-02). Los equipos del local
          // (dispensador, televisores) van fuera, como el autoservicio.
          { path: "/turnos", element: <ConPermiso alguno permiso={["pos.sell", "turnos.atender"]}>{perezosa(() => import("@/features/turnos/TurnosPage"), "TurnosPage")}</ConPermiso> },
          { path: "/turnos/atender", element: <ConPermiso alguno permiso={["pos.sell", "turnos.atender"]}>{perezosa(() => import("@/features/turnos/AtenderPage"), "AtenderPage")}</ConPermiso> },
          { path: "/publicidad", element: <ConPermiso permiso={"catalog.view"}>{perezosa(() => import("@/features/publicidad/PublicidadPage"), "PublicidadPage")}</ConPermiso> },
          { path: "/monedas", element: perezosa(() => import("@/features/monedas/MonedasPage"), "MonedasPage") },
          { path: "/equipo", element: perezosa(() => import("@/features/equipo/EquipoPage"), "EquipoPage") },
          // Junta varias áreas: con el permiso de cualquiera se entra, y cada
          // una se ve con el suyo (2026-10-06).
          { path: "/calendario", element: <ConPermiso alguno permiso={PERMISOS_DEL_CALENDARIO}>{perezosa(() => import("@/features/calendario/CalendarioPage"), "CalendarioPage")}</ConPermiso> },
          { path: "/ajustes", element: perezosa(() => import("@/features/ajustes/AjustesPage"), "AjustesPage") },
          { path: "/ajustes/negocio", element: perezosa(() => import("@/features/ajustes/DatosNegocioPage"), "DatosNegocioPage") },
          { path: "/ajustes/funciones", element: perezosa(() => import("@/features/ajustes/FuncionesPage"), "FuncionesPage") },
          { path: "/ajustes/modo", element: perezosa(() => import("@/features/ajustes/ModoPage"), "ModoPage") },
          { path: "/ajustes/restaurante", element: perezosa(() => import("@/features/ajustes/RestaurantePage"), "RestaurantePage") },
          { path: "/ajustes/consultorio", element: <ConPermiso permiso={"consultorio.configurar"}>{perezosa(() => import("@/features/consultorio/AjustesConsultorioPage"), "AjustesConsultorioPage")}</ConPermiso> },
          { path: "/ajustes/balanza", element: perezosa(() => import("@/features/ajustes/BalanzaPage"), "BalanzaPage") },
          { path: "/ajustes/cuenta", element: perezosa(() => import("@/features/ajustes/CuentaPage"), "CuentaPage") },
          { path: "/ajustes/sugerencias", element: perezosa(() => import("@/features/ajustes/SugerenciasPage"), "SugerenciasPage") },
          { path: "/ajustes/listas-de-precios", element: <ConPermiso permiso={"catalog.precios"}>{perezosa(() => import("@/features/ajustes/ListasDePreciosPage"), "ListasDePreciosPage")}</ConPermiso> },
          { path: "/catalogo/importar", element: <ConPermiso permiso={"catalog.importar"}>{perezosa(() => import("@/features/catalogo/ImportarPage"), "ImportarPage")}</ConPermiso> },
          { path: "/catalogo/importar-recetas", element: <ConPermiso permiso={"catalog.importar"}>{perezosa(() => import("@/features/catalogo/ImportarRecetasPage"), "ImportarRecetasPage")}</ConPermiso> },
          { path: "/ajustes/bot", element: perezosa(() => import("@/features/ajustes/BotPage"), "BotPage") },
          { path: "/ajustes/sucursales", element: <ConPermiso permiso={"tenant.sucursales"}>{perezosa(() => import("@/features/ajustes/SucursalesPage"), "SucursalesPage")}</ConPermiso> },
          { path: "/ajustes/sofi", element: perezosa(() => import("@/features/ajustes/SofiAjustesPage"), "SofiAjustesPage") },
          { path: "/ajustes/metodos-pago", element: <ConPermiso permiso={"tenant.metodos_pago"}>{perezosa(() => import("@/features/ajustes/MetodosPagoPage"), "MetodosPagoPage")}</ConPermiso> },
          { path: "/ajustes/impresora", element: perezosa(() => import("@/features/ajustes/ImpresoraPage"), "ImpresoraPage") },
          { path: "/ajustes/pasarela", element: perezosa(() => import("@/features/pasarela/PasarelaPage"), "PasarelaPage") },
          { path: "/tutoriales", element: perezosa(() => import("@/features/ayuda/TutorialesPage"), "TutorialesPage") },
          { path: "/premium", element: perezosa(() => import("@/features/premium/PremiumPage"), "PremiumPage") },
          { path: "/pagos-por-revisar", element: perezosa(() => import("@/features/pasarela/BandejaPage"), "BandejaPage") },
        ],
      },
    ],
  },

  /*
    ⚠️ EL CONSULTOR DE PRECIOS VA FUERA DE `AppLayout`, Y ES LO QUE LO HACE SEGURO.

    Es una tablet colgada de un pilar, al alcance de cualquier cliente. Dentro
    del menú de Softfi, dos toques llevan a las ventas del día, a los costos y
    a la caja. Aquí ocupa la pantalla entera y se sale con una clave.
  */
  {
    element: <RutaProtegida />,
    children: [
      { path: "/consultar", element: perezosa(() => import("@/features/consultor/ConsultorPage"), "ConsultorPage") },
      // El autoservicio puesto en el equipo, por la misma razón (2026-10-15).
      { path: "/autoservicio/pantalla", element: perezosa(() => import("@/features/autoservicio/PantallaPage"), "PantallaPage") },
      // El dispensador, el televisor de turnos y el de anuncios (2026-11-02).
      { path: "/turnos/dispensador", element: perezosa(() => import("@/features/turnos/DispensadorPage"), "DispensadorPage") },
      { path: "/turnos/pantalla", element: perezosa(() => import("@/features/turnos/PantallaTurnosPage"), "PantallaTurnosPage") },
      { path: "/publicidad/pantalla", element: perezosa(() => import("@/features/publicidad/PantallaPage"), "PantallaPage") },
    ],
  },

  // El turno en el teléfono del cliente (2026-11-02). Pública: se abre al
  // escanear el QR del ticket, sin cuenta. Lo identifica la llave que va detrás
  // de `#`. Ver `features/turnos/TurnoEnTelefonoPage.tsx`.
  {
    path: "/turno",
    element: perezosa(() => import("@/features/turnos/TurnoEnTelefonoPage"), "TurnoEnTelefonoPage"),
  },

  // Qué le manda de verdad un lector al navegador (2026-10-03). Pública: se
  // abre en el aparato sin iniciar sesión, y no guarda ni manda nada.
  {
    path: "/prueba-del-lector",
    element: perezosa(() => import("@/features/consultor/PruebaDelLector"), "PruebaDelLector"),
  },

  // El teléfono como escáner de una PC (2026-11-02). Pública: se abre al
  // escanear el QR de la computadora, sin iniciar sesión en el teléfono. Lo que
  // la identifica es la llave del QR. Ver `features/telefono/TelefonoPage.tsx`.
  {
    path: "/telefono",
    element: perezosa(() => import("@/features/telefono/TelefonoPage"), "TelefonoPage"),
  },

  // Cualquier otra dirección vuelve al inicio.
  {
    // ⚠️ El panel de Softfi. Pide sesión, pero quien decide de verdad quién
    // entra es el backend (`platform_admins`): esconder una ruta nunca ha
    // protegido nada. Va fuera de AppLayout porque es otro producto.
    element: <RutaProtegida />,
    children: [
      {
        element: perezosa(() => import("@/features/admin/AdminLayout"), "AdminLayout"),
        children: [
          { path: "/admin", element: perezosa(() => import("@/features/admin/ResumenPage"), "ResumenPage") },
          { path: "/admin/negocios", element: perezosa(() => import("@/features/admin/NegociosPage"), "NegociosPage") },
          { path: "/admin/ajustes", element: perezosa(() => import("@/features/admin/AjustesPlataformaPage"), "AjustesPlataformaPage") },
          { path: "/admin/cobros", element: perezosa(() => import("@/features/admin/CobrosPage"), "CobrosPage") },
          { path: "/admin/avisos-de-pago", element: perezosa(() => import("@/features/admin/AvisosDePagoPage"), "AvisosDePagoPage") },
          { path: "/admin/sucursales", element: perezosa(() => import("@/features/admin/SucursalesPorAprobarPage"), "SucursalesPorAprobarPage") },
          { path: "/admin/socios", element: perezosa(() => import("@/features/admin/SociosPage"), "SociosPage") },
        ],
      },
    ],
  },

  { path: "*", element: <Navigate to="/" replace /> },
]);
